/**
 * DialogFeatures - Custom Element wrapping native HTML dialog with additional features
 *
 * Features:
 * - close-on-click-outside: Closes dialog when clicking outside (supports modal dialogs)
 * - close-on-swipe-down: Closes dialog on mobile swipe down gesture (respects scroll positions)
 *
 * Usage:
 * <dialog-features close-on-click-outside close-on-swipe-down>
 *   <dialog>
 *     <!-- dialog content -->
 *   </dialog>
 * </dialog-features>
 */
class DialogFeatures extends HTMLElement {
  constructor() {
    super();

    // Bind methods to maintain context
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleDialogOpen = this.handleDialogOpen.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);

    // Touch tracking variables for swipe down
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.isDragging = false;
    this.swipeThreshold = 100; // Minimum pixels to trigger swipe
    this.velocityThreshold = 0.5; // Minimum velocity to trigger swipe
    this.touchStartTime = 0;
    this.canSwipeDown = false; // Flag to check if swipe down is allowed

    // Dialog reference
    this.dialog = null;
    this.isListening = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector("dialog");

    if (!this.dialog) {
      console.warn("DialogFeatures: No dialog element found");
      return;
    }

    // Set up mutation observer to detect when dialog opens/closes
    this.setupDialogObserver();

    // Check if dialog is already open
    if (this.dialog.open) {
      this.addEventListeners();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupDialogObserver() {
    if (!this.dialog) return;

    // Watch for changes to the dialog's open attribute
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "open"
        ) {
          if (this.dialog && this.dialog.open) {
            this.handleDialogOpen();
          } else {
            this.handleDialogClose();
          }
        }
      });
    });

    this.observer.observe(this.dialog, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  handleDialogOpen() {
    if (!this.isListening) {
      this.addEventListeners();
    }
  }

  handleDialogClose() {
    this.removeEventListeners();
  }

  addEventListeners() {
    if (this.isListening) return;

    this.isListening = true;

    // Close on click outside
    if (this.hasAttribute("close-on-click-outside")) {
      // Use a slight delay to prevent immediate closure from the opening click
      setTimeout(() => {
        document.addEventListener("click", this.handleOutsideClick, true);
      }, 10);
    }

    // Close on swipe down (mobile)
    if (this.hasAttribute("close-on-swipe-down") && this.dialog) {
      this.dialog.addEventListener("touchstart", this.handleTouchStart, {
        passive: false,
      });
      this.dialog.addEventListener("touchmove", this.handleTouchMove, {
        passive: false,
      });
      this.dialog.addEventListener("touchend", this.handleTouchEnd, {
        passive: false,
      });
    }
  }

  removeEventListeners() {
    if (!this.isListening) return;

    this.isListening = false;

    // Remove click outside listener
    document.removeEventListener("click", this.handleOutsideClick, true);

    // Remove touch listeners
    if (this.dialog) {
      this.dialog.removeEventListener("touchstart", this.handleTouchStart);
      this.dialog.removeEventListener("touchmove", this.handleTouchMove);
      this.dialog.removeEventListener("touchend", this.handleTouchEnd);
    }
  }

  handleTouchStart(event) {
    if (!this.dialog || !this.dialog.open) return;

    // Only track single touch
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const target = event.target;

    this.touchStartY = touch.clientY;
    this.touchCurrentY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isDragging = false;
    this.canSwipeDown = true;

    // Check scroll positions of all scrollable elements between target and dialog
    let currentElement = target;
    while (currentElement && currentElement !== this.dialog) {
      if (this.isScrollable(currentElement) && currentElement.scrollTop > 0) {
        this.canSwipeDown = false;
        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Also check the dialog itself
    if (
      this.canSwipeDown &&
      this.isScrollable(this.dialog) &&
      this.dialog.scrollTop > 0
    ) {
      this.canSwipeDown = false;
    }

    // Only start tracking if swipe down is allowed
    if (this.canSwipeDown) {
      this.isDragging = true;
    }
  }

  handleTouchMove(event) {
    if (
      !this.isDragging ||
      !this.dialog ||
      !this.dialog.open ||
      !this.canSwipeDown
    )
      return;

    if (event.touches.length !== 1) {
      this.isDragging = false;
      return;
    }

    const touch = event.touches[0];
    this.touchCurrentY = touch.clientY;

    const deltaY = this.touchCurrentY - this.touchStartY;

    // Only track downward swipes
    if (deltaY > 0) {
      // Prevent default scrolling if we're in a swipe gesture
      if (deltaY > 10) {
        event.preventDefault();
      }
    } else {
      // If swiping up, cancel the gesture
      this.isDragging = false;
    }
  }

  handleTouchEnd() {
    if (
      !this.isDragging ||
      !this.dialog ||
      !this.dialog.open ||
      !this.canSwipeDown
    ) {
      this.resetTouchTracking();
      return;
    }

    const deltaY = this.touchCurrentY - this.touchStartY;
    const deltaTime = Date.now() - this.touchStartTime;
    const velocity = deltaY / deltaTime; // pixels per millisecond

    // Check if swipe meets threshold criteria
    const isSwipeDown =
      deltaY > this.swipeThreshold || velocity > this.velocityThreshold;

    if (isSwipeDown && deltaY > 0) {
      this.closeDialog();
    }

    this.resetTouchTracking();
  }

  resetTouchTracking() {
    this.isDragging = false;
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.touchStartTime = 0;
    this.canSwipeDown = false;
  }

  isScrollable(element) {
    if (!element) return false;

    const style = getComputedStyle(element);
    const hasScrollableContent =
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth;
    const hasOverflowScroll =
      style.overflow === "scroll" ||
      style.overflow === "auto" ||
      style.overflowY === "scroll" ||
      style.overflowY === "auto" ||
      style.overflowX === "scroll" ||
      style.overflowX === "auto";

    return hasScrollableContent && hasOverflowScroll;
  }

  handleOutsideClick(event) {
    if (!this.dialog || !this.dialog.open) return;

    // Get the dialog bounds
    const dialogRect = this.dialog.getBoundingClientRect();
    const clickX = event.clientX;
    const clickY = event.clientY;

    // Check if click is outside dialog bounds
    const isOutside =
      clickX < dialogRect.left ||
      clickX > dialogRect.right ||
      clickY < dialogRect.top ||
      clickY > dialogRect.bottom;

    if (isOutside) {
      // For modal dialogs, we need to check if the click was on the backdrop
      // The backdrop is the ::backdrop pseudo-element, but we can't directly detect it
      // Instead, we check if the click target is the dialog itself (which would be the backdrop)
      const isModal = this.dialog.matches(":modal");

      if (isModal) {
        // For modal dialogs, the click target should be the dialog element itself when clicking backdrop
        if (event.target === this.dialog) {
          this.closeDialog();
        }
      } else {
        // For non-modal dialogs, any outside click should close
        this.closeDialog();
      }
    }
  }

  closeDialog() {
    if (this.dialog && this.dialog.open) {
      // Dispatch a custom event before closing
      const closeEvent = new CustomEvent("dialog-close", {
        bubbles: true,
        cancelable: true,
        detail: { source: "dialog-features" },
      });

      this.dispatchEvent(closeEvent);

      // Close the dialog if the event wasn't cancelled
      if (!closeEvent.defaultPrevented) {
        this.dialog.close();
      }
    }
  }

  // Public API methods
  open() {
    if (this.dialog) {
      this.dialog.showModal ? this.dialog.showModal() : this.dialog.show();
    }
  }

  close() {
    this.closeDialog();
  }

  toggle() {
    if (this.dialog) {
      if (this.dialog.open) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  // Getters for feature status
  get hasClickOutsideFeature() {
    return this.hasAttribute("close-on-click-outside");
  }

  get hasSwipeDownFeature() {
    return this.hasAttribute("close-on-swipe-down");
  }
}

customElements.define("dialog-features", DialogFeatures);
